import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  Signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import {
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import {
  ICustomReportFilter,
  isDataPointValue,
  ReportFilterComparison,
  TEXT_FILTER_COMPARISONS,
} from '@principle-theorem/principle-core/interfaces';
import {
  CustomReportFilter,
  ReportBuilderDataSourceInstance,
} from '@principle-theorem/reporting';
import { ICustomReportFilterFormatter } from '@principle-theorem/reporting/interfaces';
import { debounceUserInput } from '@principle-theorem/shared';
import { first } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

interface IStringFilterForm {
  comparison: ReportFilterComparison;
  values: string[];
}

@Component({
  selector: 'pr-edit-report-filter-string',
  templateUrl: './edit-report-filter-string.component.html',
  styleUrls: ['./edit-report-filter-string.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatChipsModule,
    MatIcon,
  ],
})
export class EditReportFilterStringComponent {
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  formGroup = new TypedFormGroup<IStringFilterForm>({
    comparison: new TypedFormControl<ReportFilterComparison>(
      ReportFilterComparison.TextIsEqualTo,
      Validators.required
    ),
    values: new TypedFormControl<string[]>([], Validators.required),
  });
  valueCtrl = new TypedFormControl<string>('');
  comparisons = CustomReportFilter.getComparisonOptions(
    TEXT_FILTER_COMPARISONS
  );

  dataSource = input.required<ReportBuilderDataSourceInstance>();
  filter = input.required<ICustomReportFilter>();
  filterChange = output<Partial<ICustomReportFilter>>();

  formatter: Signal<ICustomReportFilterFormatter>;
  propertyLabel: Signal<string>;
  comparisonLabel: Signal<string>;
  comparisonHint$: Observable<string | undefined>;

  constructor() {
    this.formatter = computed(() =>
      CustomReportFilter.getFormatter(
        this.dataSource().factTable,
        this.filter()
      )
    );

    const property$ = combineLatest([
      toObservable(this.dataSource),
      toObservable(this.filter).pipe(
        distinctUntilChanged((a, b) => a.id === b.id)
      ),
    ]).pipe(
      map(([dataSource, filter]) =>
        CustomReportFilter.getProperty(dataSource.factTable, filter)
      )
    );
    const propertyLabel$ = property$.pipe(
      map((property) => property?.metadata.label ?? 'Value')
    );
    this.propertyLabel = toSignal(propertyLabel$, { initialValue: 'Value' });

    effect(() => {
      const formData = this._toFormData(this.filter());
      this.formGroup.patchValue(formData, { emitEvent: false });
    });

    validFormGroupChanges$(this.formGroup)
      .pipe(
        debounceUserInput(),
        map((value) => this._fromFormData(value)),
        takeUntilDestroyed()
      )
      .subscribe((filterChanges) => this.filterChange.emit(filterChanges));

    const comparison$ = validFormGroupChanges$(this.formGroup).pipe(
      startWith(this.formGroup.value),
      map((formValue) => formValue.comparison),
      distinctUntilChanged()
    );
    this.comparisonHint$ = combineLatest([propertyLabel$, comparison$]).pipe(
      map(([propertyLabel, comparison]) =>
        this._getComparisonHint(propertyLabel, comparison)
      )
    );
  }

  getComparisonLabel(comparison: ReportFilterComparison): string {
    const option = CustomReportFilter.getComparisonOption(comparison);
    return option.label;
  }

  remove(value: string): void {
    const formCtrl = this.formGroup.controls.values;
    const currentValues = formCtrl.value || [];
    const updatedValues = currentValues.filter(
      (currentValue) => currentValue !== value
    );
    formCtrl.setValue(updatedValues, { emitEvent: true });
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      const formCtrl = this.formGroup.controls.values;
      const currentValues = formCtrl.value || [];
      const updatedValues = [...currentValues, value];
      formCtrl.setValue(updatedValues, { emitEvent: true });
    }
    event.chipInput.clear();
  }

  private _toFormData(filter: ICustomReportFilter): Partial<IStringFilterForm> {
    const values = filter.values
      .map((value) => value.value)
      .filter(isDataPointValue)
      .map((value) => `${value}`);
    const comparison = this._getValidComparison(filter);
    return { comparison, values };
  }

  private _getValidComparison(
    filter: ICustomReportFilter
  ): ReportFilterComparison {
    const validComparisons = this.comparisons.map(
      (comparison) => comparison.value
    );
    const comparison = CustomReportFilter.getComparison(filter);
    if (comparison && validComparisons.includes(comparison)) {
      return comparison;
    }
    return first(validComparisons) ?? ReportFilterComparison.IsEqualTo;
  }

  private _fromFormData(
    formData: IStringFilterForm
  ): Partial<ICustomReportFilter> {
    return {
      comparison: formData.comparison,
      values: formData.values.map((value) => ({ label: `${value}`, value })),
      dateType: undefined,
      relativeOffset: undefined,
    };
  }

  private _getComparisonHint(
    propertyLabel: string,
    comparison: ReportFilterComparison
  ): string | undefined {
    switch (comparison) {
      case ReportFilterComparison.TextIsEqualTo:
        return `${propertyLabel} must equal one of the values entered. (Case insensitive).`;
      case ReportFilterComparison.TextIsExactly:
        return `${propertyLabel} must match one of the values entered exactly. (Case sensitive).`;
      case ReportFilterComparison.TextContains:
        return `${propertyLabel} must contain one of the values entered. (Case sensitive).`;
      case ReportFilterComparison.TextStartsWith:
        return `${propertyLabel} must start with one of the values entered. (Case sensitive).`;
      case ReportFilterComparison.TextEndsWith:
        return `${propertyLabel} must end with one of the values entered. (Case sensitive).`;
      default:
        return;
    }
  }
}
